import React, { useState, useEffect, useRef } from "react";
import apiAxios from "../../../services/apiAxios";
import toast from "react-hot-toast";
import Edit from "../../common/icons/Edit";
import CheckedBg from "../../common/icons/CheckedBg";
import CloseBg from "../../common/icons/CloseBg";
import { useSelector } from "react-redux";
import AdminSidebarToggle from "../AdminSidebarToggle";

const PrescriptionModeration = () => {
    const isSidebarVisible = useSelector((state) => state.admin?.isSidebarVisible);
    const [parameters, setParameters] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filters
    const [filterStatus, setFilterStatus] = useState("pending");
    const [filterType, setFilterType] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedDocIds, setSelectedDocIds] = useState([]);
    const [selectedSpecialties, setSelectedSpecialties] = useState([]);
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [showTypeDropdown, setShowTypeDropdown] = useState(false);
    const [showSpecDropdown, setShowSpecDropdown] = useState(false);
    const [showDocDropdown, setShowDocDropdown] = useState(false);
    const statusDropdownRef = useRef(null);
    const typeDropdownRef = useRef(null);
    const specDropdownRef = useRef(null);
    const docDropdownRef = useRef(null);

    // Edit Modal State
    const [editingParam, setEditingParam] = useState(null);
    const [unsavedImageUrl, setUnsavedImageUrl] = useState(null); // Track new uploads not yet saved to DB
    const [previewImage, setPreviewImage] = useState(null); // Full-screen image preview

    const specialties = [
        "Orthopaedics", "Cardiology", "Dermatology", "Neurology",
        "Endocrinology", "Pulmonology", "Urology", "Ophthalmology",
        "Psychiatry", "Geriatric Medicine", "General Physician", "General"
    ];

    const types = [
        { value: "complaint", label: "Complaint" },
        { value: "diagnosis", label: "Diagnosis" },
        { value: "test", label: "Test" },
        { value: "measurement", label: "Measurement" },
        { value: "observation", label: "Observation" },
        { value: "advice", label: "Advice" },
        { value: "treatment", label: "Treatment" },
        { value: "diagram", label: "Diagram" },
    ];

    const statuses = [
        { value: "pending", label: "⏳ Pending Review", color: "text-amber-600 bg-amber-50" },
        { value: "approved", label: "✅ Approved", color: "text-success-600 bg-success-50" },
        { value: "rejected", label: "❌ Rejected", color: "text-danger-600 bg-danger-50" },
    ];

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (specDropdownRef.current && !specDropdownRef.current.contains(e.target)) {
                setShowSpecDropdown(false);
            }
            if (docDropdownRef.current && !docDropdownRef.current.contains(e.target)) {
                setShowDocDropdown(false);
            }
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
                setShowStatusDropdown(false);
            }
            if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target)) {
                setShowTypeDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch doctors for the filter dropdown
            const docRes = await apiAxios.get("/api/admin/doctor/getAll");
            if (Array.isArray(docRes.data)) setDoctors(docRes.data);

            // Fetch parameters
            const params = {
                status: filterStatus,
                type: filterType,
                name: searchTerm,
                did: selectedDocIds.length > 0 ? selectedDocIds : undefined,
                specialisationCategory: selectedSpecialties.length > 0 ? selectedSpecialties : undefined
            };
            const res = await apiAxios.get("/api/global-parameters/admin/list", { params });
            if (res.data?.success) {
                setParameters(res.data.parameters);
            }
        } catch (err) {
            console.error("Failed to fetch data", err);
            toast.error("Failed to load parameters");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterStatus, filterType, searchTerm, selectedDocIds, selectedSpecialties]);

    const handleCancel = () => {
        if (unsavedImageUrl) {
            apiAxios.delete(`/api/global-parameters/delete-image?url=${encodeURIComponent(unsavedImageUrl)}`).catch(e => console.warn("Cleanup failed", e));
            setUnsavedImageUrl(null);
        }
        setEditingParam(null);
    };

    const handleAction = async (id, status, updatedData = {}) => {
        try {
            let res;
            if (id) {
                // Update existing
                res = await apiAxios.put(`/api/global-parameters/${id}`, { status, ...updatedData });
            } else {
                // Create new
                res = await apiAxios.post("/api/global-parameters/", { status, ...updatedData });
            }

            if (res.data?.success) {
                toast.success(`Parameter ${id ? status : "created"} successfully`);
                setUnsavedImageUrl(null); // Mark as saved, so it's not deleted
                fetchData();
                setEditingParam(null);
            }
        } catch (err) {
            console.error("Action failed", err);
            toast.error("Operation failed");
        }
    };

    const toggleSpecialty = (spec) => {
        setSelectedSpecialties(prev =>
            prev.includes(spec) ? prev.filter(s => s !== spec) : [...prev, spec]
        );
    };

    const toggleDoctor = (did) => {
        setSelectedDocIds(prev =>
            prev.includes(did) ? prev.filter(d => d !== did) : [...prev, did]
        );
    };

    const getDoctorName = (did) => {
        if (!did || did === "system") return "System (Admin)";
        const doc = doctors.find(d => d.DID === did);
        if (doc) {
            const name = doc.personalInfo?.name || doc.name || "";
            const surname = doc.personalInfo?.surname || doc.surname || "";
            return `Dr. ${name} ${surname}`.trim() || did;
        }
        return did;
    };

    const [isUploading, setIsUploading] = useState(false);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);
        const formData = new FormData();
        formData.append("image", file);

        try {
            const res = await apiAxios.post("/api/global-parameters/upload-diagram", formData);
            if (res.data.success) {
                // If there was an previous unsaved upload in this session, clean it up first
                if (unsavedImageUrl) {
                    apiAxios.delete(`/api/global-parameters/delete-image?url=${encodeURIComponent(unsavedImageUrl)}`).catch(e => console.warn("Cleanup failed", e));
                }

                const newUrl = res.data.imageUrl;
                setEditingParam(prev => ({ ...prev, imageUrl: newUrl }));
                setUnsavedImageUrl(newUrl);
                toast.success("Image uploaded successfully!");
            }
        } catch (error) {
            console.error("Upload error:", error);
            const errMsg = error.response?.data?.error || error.message || "Failed to upload image.";
            toast.error(errMsg);
        } finally {
            setIsUploading(false);
        }
    };

    const handleDownloadImage = async () => {
        if (!editingParam?.imageUrl) return;
        try {
            // Use backend proxy to bypass S3 CORS restrictions
            const proxyUrl = `/api/global-parameters/proxy-download?url=${encodeURIComponent(editingParam.imageUrl)}`;
            const res = await apiAxios.get(proxyUrl, { responseType: "blob" });

            const blobUrl = window.URL.createObjectURL(res.data);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = `${editingParam.name.replace(/\s+/g, "_")}_diagram.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Download failed:", error);
            toast.error("Failed to download image. Please try again later.");
        }
    };

    const handleAddNew = () => {
        setEditingParam({
            name: "",
            subtitle: "",
            imageUrl: "",
            type: "complaint",
            specialisationCategory: "General",
            status: "approved",
            did: "system"
        });
    };

    return (
        <div className="min-h-[85vh] bg-[#fdfdfd] rounded-[24px] border border-gray-100 shadow-sm p-6 sm:p-8">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-10">
                <div className="flex items-center gap-4">
                    <AdminSidebarToggle />
                    {!isSidebarVisible && <div className="h-6 w-[1.5px] bg-primary-100 mx-2 ml-1" />}
                    <div className="w-14 h-14 bg-primary-600 rounded-[18px] flex items-center justify-center text-white shadow-lg shadow-primary-100 rotate-3">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="-rotate-3">
                            <path d="M14 2H6C4.89 2 4 2.9 4 4V20C4 21.1 4.89 22 6 22H18C19.11 22 20 21.1 20 20V8L14 2ZM13 9V3.5L18.5 9H13Z" fill="currentColor" />
                        </svg>
                    </div>
                    <div>
                        <h2 className="text-[24px] font-extrabold text-gray-900 tracking-tight leading-none mb-1.5">Prescription Database</h2>
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-success-500 rounded-full animate-pulse"></span>
                            <p className="text-[13px] text-gray-400 font-medium">Standardizing medical data for Hyperapt Health partners.</p>
                        </div>
                    </div>
                </div>

                <button
                    onClick={handleAddNew}
                    className="h-[46px] px-8 bg-primary-600 text-white rounded-[14px] text-[14px] font-bold flex items-center gap-2.5 hover:bg-primary-700 hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-primary-100/50"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Add New Parameter
                </button>
            </div>

            {/* Filter Bar */}
            <div className="flex flex-wrap items-center gap-3 mb-8 p-2 bg-gray-50/50 rounded-[18px] border border-gray-100/50">
                <div className="flex-1 min-w-[200px] relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                    </div>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by parameter name..."
                        className="w-full h-[44px] pl-11 pr-4 bg-white border border-gray-100 rounded-[12px] text-[13px] outline-none focus:border-primary-400 transition-all shadow-sm shadow-black/[0.02]"
                    />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {/* Status Dropdown */}
                    <div className="relative" ref={statusDropdownRef}>
                        <button
                            onClick={() => { setShowStatusDropdown(!showStatusDropdown); setShowTypeDropdown(false); setShowDocDropdown(false); setShowSpecDropdown(false); }}
                            className={`h-[44px] px-4 bg-white border ${showStatusDropdown ? 'border-primary-500 shadow-primary-50' : 'border-gray-100'} rounded-[12px] text-[13px] font-bold text-gray-600 flex items-center justify-between gap-3 hover:border-primary-400 transition-all shadow-sm shadow-black/[0.02] min-w-[150px]`}
                        >
                            <span>{filterStatus ? statuses.find(s => s.value === filterStatus)?.label.replace(/⏳ |✅ |❌ /, '') : "All Status"}</span>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" className={`transition-transform duration-300 ${showStatusDropdown ? "rotate-180" : ""}`}>
                                <path d="M7 10l5 5 5-5z" fill="#9ca3af" />
                            </svg>
                        </button>
                        {showStatusDropdown && (
                            <div className="absolute left-0 mt-2 w-[180px] bg-white border border-gray-100 rounded-[14px] shadow-xl z-[60] overflow-hidden py-1">
                                <div onClick={() => { setFilterStatus(""); setShowStatusDropdown(false); }} className={`px-4 py-2.5 text-[13px] hover:bg-gray-50 cursor-pointer font-medium ${!filterStatus ? "text-primary-600 bg-primary-50/30" : "text-gray-500"}`}>All Records</div>
                                {statuses.map(s => (
                                    <div
                                        key={s.value}
                                        onClick={() => { setFilterStatus(s.value); setShowStatusDropdown(false); }}
                                        className={`px-4 py-2.5 text-[13px] hover:bg-gray-50 cursor-pointer font-medium ${filterStatus === s.value ? "text-primary-600 bg-primary-50/30" : "text-gray-500"}`}
                                    >
                                        {s.label}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Type Dropdown */}
                    <div className="relative" ref={typeDropdownRef}>
                        <button
                            onClick={() => { setShowTypeDropdown(!showTypeDropdown); setShowStatusDropdown(false); setShowDocDropdown(false); setShowSpecDropdown(false); }}
                            className={`h-[44px] px-4 bg-white border ${showTypeDropdown ? 'border-primary-500 shadow-primary-50' : 'border-gray-100'} rounded-[12px] text-[13px] font-bold text-gray-600 flex items-center justify-between gap-3 hover:border-primary-400 transition-all shadow-sm shadow-black/[0.02] min-w-[130px]`}
                        >
                            <span className="capitalize">{filterType || "All Types"}</span>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" className={`transition-transform duration-300 ${showTypeDropdown ? "rotate-180" : ""}`}>
                                <path d="M7 10l5 5 5-5z" fill="#9ca3af" />
                            </svg>
                        </button>
                        {showTypeDropdown && (
                            <div className="absolute left-0 mt-2 w-[160px] bg-white border border-gray-100 rounded-[14px] shadow-xl z-[60] overflow-hidden py-1">
                                <div onClick={() => { setFilterType(""); setShowTypeDropdown(false); }} className={`px-4 py-2.5 text-[13px] hover:bg-gray-50 cursor-pointer font-medium ${!filterType ? "text-primary-600 bg-primary-50/30" : "text-gray-500"}`}>All Types</div>
                                {types.map(t => (
                                    <div
                                        key={t.value}
                                        onClick={() => { setFilterType(t.value); setShowTypeDropdown(false); }}
                                        className={`px-4 py-2.5 text-[13px] hover:bg-gray-50 cursor-pointer font-medium ${filterType === t.value ? "text-primary-600 bg-primary-50/30" : "text-gray-500"}`}
                                    >
                                        {t.label}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Doctors Select */}
                    <div className="relative" ref={docDropdownRef}>
                        <button
                            onClick={() => { setShowDocDropdown(!showDocDropdown); setShowStatusDropdown(false); setShowTypeDropdown(false); setShowSpecDropdown(false); }}
                            className={`h-[44px] px-4 bg-white border ${showDocDropdown ? 'border-primary-500 shadow-primary-50' : 'border-gray-100'} rounded-[12px] text-[13px] font-bold text-gray-600 flex items-center justify-between gap-3 hover:border-primary-400 transition-all shadow-sm shadow-black/[0.02] min-w-[140px]`}
                        >
                            <span>Doctors ({selectedDocIds.length || "All"})</span>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" className={`transition-transform duration-300 ${showDocDropdown ? "rotate-180" : ""}`}>
                                <path d="M7 10l5 5 5-5z" fill="#9ca3af" />
                            </svg>
                        </button>
                        {showDocDropdown && (
                            <div className="absolute right-0 mt-2 w-[260px] bg-white border border-gray-100 rounded-[16px] shadow-2xl z-[60] overflow-hidden max-h-[350px] overflow-y-auto">
                                <div className="p-4 border-b border-gray-50 flex justify-between items-center bg-gray-50/50">
                                    <span className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Filter Doctors</span>
                                    {selectedDocIds.length > 0 && <button onClick={() => setSelectedDocIds([])} className="text-[11px] text-primary-600 font-bold hover:underline">RESET</button>}
                                </div>
                                <div className="p-2 flex flex-col gap-1">
                                    <label className="flex items-center gap-3 px-3 py-2 rounded-[10px] hover:bg-primary-50/50 cursor-pointer transition-all">
                                        <input type="checkbox" checked={selectedDocIds.includes("system")} onChange={() => toggleDoctor("system")} className="w-4 h-4 accent-primary-600" />
                                        <span className="text-[13px] text-gray-600 font-bold italic">System (Admin)</span>
                                    </label>
                                    {doctors.map(doc => (
                                        <label key={doc.DID} className="flex items-center gap-3 px-3 py-2 rounded-[10px] hover:bg-primary-50/50 cursor-pointer transition-all">
                                            <input type="checkbox" checked={selectedDocIds.includes(doc.DID)} onChange={() => toggleDoctor(doc.DID)} className="w-4 h-4 accent-primary-600" />
                                            <span className="text-[13px] text-gray-600 font-medium">Dr. {doc.personalInfo?.name || doc.name} {doc.personalInfo?.surname || doc.surname}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Spec Select */}
                    <div className="relative" ref={specDropdownRef}>
                        <button
                            onClick={() => { setShowSpecDropdown(!showSpecDropdown); setShowStatusDropdown(false); setShowTypeDropdown(false); setShowDocDropdown(false); }}
                            className={`h-[44px] px-4 bg-white border ${showSpecDropdown ? 'border-primary-500 shadow-primary-50' : 'border-gray-100'} rounded-[12px] text-[13px] font-bold text-gray-600 flex items-center justify-between gap-3 hover:border-primary-400 transition-all shadow-sm shadow-black/[0.02] min-w-[140px]`}
                        >
                            <span>Spec. ({selectedSpecialties.length || "All"})</span>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" className={`transition-transform duration-300 ${showSpecDropdown ? "rotate-180" : ""}`}>
                                <path d="M7 10l5 5 5-5z" fill="#9ca3af" />
                            </svg>
                        </button>
                        {showSpecDropdown && (
                            <div className="absolute right-0 mt-2 w-[240px] bg-white border border-gray-100 rounded-[16px] shadow-2xl z-[60] overflow-hidden max-h-[350px] overflow-y-auto">
                                <div className="p-4 border-b border-gray-50 flex justify-between items-center bg-gray-50/50">
                                    <span className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Filter Specialty</span>
                                    {selectedSpecialties.length > 0 && <button onClick={() => setSelectedSpecialties([])} className="text-[11px] text-primary-600 font-bold hover:underline">RESET</button>}
                                </div>
                                <div className="p-2 grid grid-cols-1 gap-1">
                                    {specialties.map(spec => (
                                        <label key={spec} className="flex items-center gap-3 px-3 py-2 rounded-[10px] hover:bg-primary-50/50 cursor-pointer transition-all">
                                            <input type="checkbox" checked={selectedSpecialties.includes(spec)} onChange={() => toggleSpecialty(spec)} className="w-4 h-4 accent-primary-600" />
                                            <span className="text-[13px] text-gray-600 font-medium">{spec}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Table Area */}
            <div className="overflow-hidden bg-white border border-gray-100 rounded-[20px] shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse table-fixed">
                        <thead>
                            <tr className="bg-gray-50/30">
                                <th className="w-[35%] px-7 py-5 text-left text-[11px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">Parameter Name</th>
                                <th className="w-[12%] px-4 py-5 text-center text-[11px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">Type</th>
                                <th className="w-[12%] px-4 py-5 text-center text-[11px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">Status</th>
                                <th className="w-[20%] px-4 py-5 text-left text-[11px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">Suggested By</th>
                                <th className="w-[11%] px-4 py-5 text-center text-[11px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="text-[13px] text-gray-700 divide-y divide-gray-50">
                            {loading ? (
                                <tr><td colSpan="5" className="px-7 py-24 text-center"><div className="flex flex-col items-center gap-3"><div className="w-8 h-8 border-4 border-primary-100 border-t-primary-600 rounded-full animate-spin"></div><span className="text-[13px] font-bold text-gray-400">Syncing database...</span></div></td></tr>
                            ) : parameters.length === 0 ? (
                                <tr><td colSpan="5" className="px-7 py-24 text-center text-[14px] text-gray-400 font-medium italic">No parameters found matches your selection.</td></tr>
                            ) : (
                                parameters.map((param) => (
                                    <tr key={param.id} className="group hover:bg-primary-50/10 transition-colors">
                                        <td className="px-7 py-5 truncate">
                                            <div className="font-extrabold text-gray-900 text-[15px] group-hover:text-primary-600 transition-colors" title={param.name}>{param.name}</div>
                                            {param.specialisationCategory && <div className="text-[11px] text-gray-400 font-bold mt-0.5">{param.specialisationCategory}</div>}
                                        </td>
                                        <td className="px-4 py-5 text-center">
                                            <span className="px-3 py-1 bg-gray-50 text-gray-500 rounded-[8px] text-[10px] font-black uppercase tracking-wider border border-gray-100">{param.type}</span>
                                        </td>
                                        <td className="px-4 py-5 text-center">
                                            <span className={`px-3 py-1 rounded-[8px] text-[10px] font-black uppercase tracking-wider whitespace-nowrap ${param.status === 'approved' ? 'text-success-600 bg-success-50/50 border border-success-100' : param.status === 'pending' ? 'text-amber-600 bg-amber-50/50 border border-amber-100' : 'text-danger-600 bg-danger-50/50 border border-danger-100'}`}>
                                                {param.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-5">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-9 h-9 rounded-[12px] flex-shrink-0 flex items-center justify-center text-[12px] font-black border transition-transform group-hover:scale-110 ${param.did === "system" ? "bg-amber-50 text-amber-600 border-amber-100/50" : "bg-primary-50 text-primary-600 border-primary-100/50"}`}>
                                                    {param.did === "system" ? "S" : "DR"}
                                                </div>
                                                <div className="overflow-hidden">
                                                    <div className="font-bold text-gray-900 group-hover:text-primary-600 transition-colors truncate">{getDoctorName(param.did)}</div>
                                                    <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{param.did === 'system' ? 'Master Dataset' : 'Verified Partner'}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-5">
                                            <div className="flex justify-center gap-2">
                                                {param.status === "pending" && (
                                                    <>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleAction(param.id, "approved"); }}
                                                            className="w-10 h-10 flex items-center justify-center bg-white border border-gray-100 text-success-500 rounded-[12px] hover:bg-success-600 hover:text-white hover:border-success-600 transition-all shadow-sm"
                                                            title="Approve"
                                                        >
                                                            <CheckedBg height={18} width={18} fill="currentColor" />
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleAction(param.id, "rejected"); }}
                                                            className="w-10 h-10 flex items-center justify-center bg-white border border-gray-100 text-danger-500 rounded-[12px] hover:bg-danger-600 hover:text-white hover:border-danger-600 transition-all shadow-sm"
                                                            title="Reject"
                                                        >
                                                            <CloseBg height={18} width={18} fill="currentColor" />
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    onClick={() => setEditingParam(param)}
                                                    className="w-10 h-10 flex items-center justify-center bg-white border border-gray-100 text-primary-600 rounded-[12px] hover:bg-primary-600 hover:text-white hover:border-primary-600 transition-all shadow-sm font-bold"
                                                    title="Edit"
                                                >
                                                    <Edit height={18} width={18} fill="currentColor" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit Modal */}
            {editingParam && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <div className="bg-white rounded-[16px] w-full max-w-[500px] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-6 bg-primary-600 text-white">
                            <h3 className="text-[18px] font-bold">Edit Parameter</h3>
                            <p className="text-[13px] opacity-80 mt-1">Refine medication name and categorization before approval.</p>
                        </div>
                        <div className="p-6 flex flex-col gap-5">
                            <div>
                                <label className="block text-[13px] font-semibold text-primary-800 mb-2">Parameter Name</label>
                                <input
                                    type="text"
                                    value={editingParam.name}
                                    onChange={(e) => setEditingParam({ ...editingParam, name: e.target.value })}
                                    className="w-full h-[40px] px-3 border border-primary-200 rounded-[8px] outline-none focus:border-primary-500"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[13px] font-semibold text-primary-800 mb-2">Category Type</label>
                                    <select
                                        value={editingParam.type}
                                        onChange={(e) => setEditingParam({ ...editingParam, type: e.target.value })}
                                        className="w-full h-[40px] px-2 border border-primary-200 rounded-[8px] outline-none focus:border-primary-500"
                                    >
                                        <option value="complaint">Complaint</option>
                                        <option value="diagnosis">Diagnosis</option>
                                        <option value="test">Test</option>
                                        <option value="observation">Observation</option>
                                        <option value="measurement">Measurement</option>
                                        <option value="advice">Advice</option>
                                        <option value="treatment">Treatment</option>
                                        <option value="diagram">Diagram</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[13px] font-semibold text-primary-800 mb-2">Specialization</label>
                                    <select
                                        value={editingParam.specialisationCategory || ""}
                                        onChange={(e) => setEditingParam({ ...editingParam, specialisationCategory: e.target.value })}
                                        className="w-full h-[40px] px-2 border border-primary-200 rounded-[8px] outline-none focus:border-primary-500"
                                    >
                                        <option value="">None</option>
                                        {specialties.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-[13px] font-semibold text-primary-800 mb-2">Subtitle / Caption (Optional)</label>
                                <input
                                    type="text"
                                    value={editingParam.subtitle || ""}
                                    onChange={(e) => setEditingParam({ ...editingParam, subtitle: e.target.value })}
                                    className="w-full h-[40px] px-3 border border-primary-200 rounded-[8px] outline-none focus:border-primary-500"
                                    placeholder="e.g. Anterior View, Lateral View, etc."
                                />
                            </div>

                            {editingParam.type === "diagram" && (
                                <div className="p-4 border border-primary-100 rounded-[12px] bg-primary-25/30">
                                    <label className="block text-[12px] font-bold text-primary-600 mb-3 uppercase tracking-wider">Diagram Image</label>
                                    <div className="flex items-center gap-4">
                                        <div
                                            className="w-20 h-20 rounded-[12px] border border-primary-100 bg-white p-1 overflow-hidden flex-shrink-0 cursor-zoom-in hover:border-primary-300 transition-all"
                                            onClick={() => editingParam.imageUrl && setPreviewImage({ url: editingParam.imageUrl, title: editingParam.name, subtitle: editingParam.subtitle })}
                                            title="Click to enlarge"
                                        >
                                            {editingParam.imageUrl ? (
                                                <img src={editingParam.imageUrl} className="w-full h-full object-contain" alt="Preview" />
                                            ) : (
                                                <div className="w-full h-full bg-gray-50 flex items-center justify-center text-gray-300">
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <div className="relative">
                                                <button className={`h-[36px] px-4 w-full rounded-[8px] border text-[12px] font-bold transition-all ${isUploading ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-primary-50 text-primary-600 border-primary-200 hover:bg-primary-100'}`}>
                                                    {isUploading ? "Uploading..." : editingParam.imageUrl ? "Replace Image" : "Upload Image"}
                                                </button>
                                                {!isUploading && (
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleImageUpload}
                                                        className="absolute inset-0 opacity-0 cursor-pointer"
                                                    />
                                                )}
                                            </div>
                                            {editingParam.imageUrl && (
                                                <button
                                                    type="button"
                                                    onClick={handleDownloadImage}
                                                    className="mt-2 w-full h-[32px] flex items-center justify-center gap-2 text-[11px] font-bold text-gray-500 hover:text-primary-600 transition-colors"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                                                    Download Original
                                                </button>
                                            )}
                                            <p className="text-[10px] text-gray-400 mt-2 text-center">Recommended: Square Aspect Ratio, .png or .jpg</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-[13px] font-semibold text-primary-800 mb-2">Force Status (Optional)</label>
                                <select
                                    value={editingParam.status}
                                    onChange={(e) => setEditingParam({ ...editingParam, status: e.target.value })}
                                    className="w-full h-[40px] px-2 border border-primary-200 rounded-[8px] outline-none focus:border-primary-500"
                                >
                                    <option value="pending">Pending</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                            </div>
                        </div>
                        <div className="p-6 pt-0 flex gap-3">
                            <button
                                onClick={handleCancel}
                                className="flex-1 h-[40px] rounded-[8px] border border-primary-200 text-[14px] font-medium text-secondry-400 hover:bg-primary-50 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleAction(editingParam.id, editingParam.status, {
                                    name: editingParam.name,
                                    type: editingParam.type,
                                    specialisationCategory: editingParam.specialisationCategory,
                                    subtitle: editingParam.subtitle,
                                    imageUrl: editingParam.imageUrl
                                })}
                                disabled={isUploading}
                                className="flex-1 h-[40px] rounded-[8px] bg-primary-600 text-white text-[14px] font-medium hover:bg-primary-700 shadow-md shadow-primary-200 transition-all disabled:opacity-50"
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Full-screen Image Preview Lightbox */}
            {previewImage && (
                <div className="hm-overlay" onClick={() => setPreviewImage(null)} style={{ zIndex: 9999 }}>
                    <div className="hm-overlay-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "min(90vw, 800px)", background: "#1a1a1a", padding: "12px", borderRadius: "24px", border: "1px solid rgba(255,255,255,0.12)", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.7)" }}>
                        <button className="hm-overlay-close" onClick={() => setPreviewImage(null)} style={{ top: -45, right: 0 }}>✕</button>
                        <div style={{ display: "flex", flexDirection: "column", overflow: "hidden", borderRadius: "16px" }}>
                            <img
                                src={previewImage.url || previewImage}
                                alt={previewImage.title || "Full Preview"}
                                style={{ width: "100%", height: "auto", display: "block" }}
                            />
                            {(previewImage.title || previewImage.subtitle) && (
                                <div style={{ color: "#fff", textAlign: "center", display: "flex", flexDirection: "column", gap: 2, background: "rgba(255,255,255,0.03)", padding: "16px 12px" }}>
                                    {previewImage.title && <div style={{ fontSize: 18, fontWeight: 700 }}>{previewImage.title}</div>}
                                    {previewImage.subtitle && (
                                        <div style={{ fontSize: 14, opacity: 0.8 }}>{previewImage.subtitle}</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PrescriptionModeration;
